import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Button, Typography } from '@mui/material'
import { useReadUserProfile } from '@/api/user-manager'
import {
  isProposalExpired,
  useReadApplication,
} from '@/api/financial/application/read-application'
import {
  useColleges,
  useSetApplicationCollege,
} from '@/api/financial/application/college'
import {
  useInternshipPreview,
  useSaveInternshipSimulation,
} from '@/api/financial/simulation/simulate-internship'
import {
  useCompleteProponents,
  usePatchPerson,
} from '@/api/financial/person/update-person'
import {
  ApplicationPersonType,
  useCreateApplicationPerson,
  useDetachApplicationPerson,
  useSetApplicationPersonTypes,
} from '@/api/financial/person/application-person'
import { useMunicipalities } from '@/api/financial/person/municipalities'
import {
  useDeleteDocument,
  useUploadDocument,
} from '@/api/financial/file/documents'
import {
  useAcceptProposal,
  usePreAnalysis,
  useReissueProposal,
  useSendCommittee,
} from '@/api/financial/committee/committee'
import { JourneySheet } from '@/components/credit-journey/journey-sheet'
import { JourneyShell } from '@/components/credit-journey/journey-shell'
import { formatTaxId } from '@/helpers'
import {
  hydrationStamp,
  shouldRehydrate,
} from '@/pages/credit/journeys/rehydrate'
import { saveProfile } from '@/pages/session/profile/save-profile'
import { estimatedSemester } from '@/pages/session/profile/profile-steps'
import { creditProducts } from '@/pages/credit/products/credit-products'
import { routes } from '@/routes'
import { CepResponse } from '@/types'
import {
  BackerRole,
  ENABLING_LETTER,
  InternshipStep,
  backerDocuments,
  editorRole,
  internshipSteps,
  minGuarantorIncome,
  previousStep,
  resolveStep,
  stageOf,
  stepProgress,
  studentDocuments,
  studentSteps,
  visibleSteps,
} from './internship-flow'
import {
  BackerForm,
  InternshipForm,
  backerFromPerson,
  backerLinks,
  backerPayloadForStep,
  clearDraft,
  clearDraftKeys,
  digits,
  emptyBacker,
  emptyForm,
  formFromApplication,
  isStepValid,
  readDraft,
  studentLink,
  studentPayloadForStep,
  writeDraft,
} from './internship-form'
import {
  clearConfirmedSteps,
  useConfirmedSteps,
} from '@/pages/credit/journeys/confirmed-steps'
import {
  DocumentPerson,
  InternshipScreens,
  backerRows,
} from './internship-screens'
import { JourneySkeleton } from '@/components/credit-journey/journey-skeleton'

const titles: Record<InternshipStep, string> = {
  fac: 'Dados sobre sua faculdade',
  sem: 'Em qual semestre quer começar o financiamento?',
  divida: 'Você tem dívidas com a faculdade?',
  res: 'Seu financiamento ficaria assim:',
  eu: 'Seus dados',
  end: 'Onde você mora?',
  civil: 'Estado civil e declarações',
  traj: 'Sua trajetória',
  planos: 'Quais são seus planos logo após a formatura?',
  fin: 'Como a faculdade foi paga até agora?',
  resp: 'Quem é seu responsável financeiro?',
  r1: 'Quem é o responsável financeiro?',
  r2: 'Contato do responsável',
  r3: 'Renda do responsável',
  r4: 'Endereço do responsável',
  rlist: 'Responsáveis financeiros',
  av: 'Quem serão seus avalistas?',
  g1: 'Quem é o avalista?',
  g2: 'Contato do avalista',
  g3: 'Renda do avalista',
  g4: 'Endereço do avalista',
  scr: 'Autorizações para a pré-análise',
  pre: '',
  docs: 'Envie os documentos',
  carta: 'Carta de habilitação',
  analise: '',
  proposta: 'Seu financiamento foi aprovado!',
  assinar: 'Assine a CCB',
  fim: '',
}

const hints: Partial<Record<InternshipStep, string>> = {
  fac: 'Vamos encontrar o financiamento ideal para você.',
  res: 'Confira a simulação e clique em continuar.',
  resp: 'É a pessoa que hoje paga a maior parte da faculdade e dos seus custos de vida. É preciso ter ao menos 1.',
  r1: 'É a pessoa que hoje paga a maior parte da faculdade e dos seus custos de vida.',
  r2: 'Usamos esse contato para a autorização do SCR e o contrato.',
  g1: 'É quem se compromete a pagar caso você e o responsável não consigam.',
  g2: 'Usamos esse contato para a autorização do SCR e o contrato.',
  av: 'Você pode indicar até 4 avalistas.',
  scr: 'Antes dos documentos, fazemos uma pré-análise. Todos precisam autorizar a consulta ao SCR do Banco Central.',
  carta:
    'Precisamos da carta de habilitação assinada para seguir com a análise.',
  proposta: 'Confira a proposta antes de aceitar.',
}

const nextLabels: Partial<Record<InternshipStep, string>> = {
  av: 'Salvar e ir para documentos',
  scr: 'Autorizar e continuar',
  pre: 'Ir para o Resumo',
  docs: 'Enviar documentos',
  carta: 'Enviar para análise',
  proposta: 'Aceitar proposta',
  analise: 'Ir para o Resumo',
  assinar: 'Aguardando a CCB',
  fim: 'Ir para o Resumo',
}

/** Campos que cada etapa salva no financial: saem do rascunho local depois de gravados. */
const stepKeys: Partial<Record<InternshipStep, (keyof InternshipForm)[]>> = {
  fac: ['collegeKey', 'graduation'],
  res: ['tuition', 'startSemester', 'paidTuitions', 'hasDebt', 'debt'],
  eu: ['birthdate', 'gender'],
  end: [
    'zip_code',
    'street',
    'district',
    'number',
    'complement',
    'municipality_id',
    'address_state',
  ],
  civil: [
    'marital',
    'spouse_name',
    'spouse_tax_id',
    'politically_exposed',
    'us_person',
  ],
  traj: [
    'high_school_type',
    'academic_award',
    'academic_award_description',
    'course_locked',
    'course_locked_reason',
  ],
  planos: ['plans', 'plans_other', 'residency_timing'],
  fin: ['payment_sources', 'payment_story', 'financing_reason'],
  resp: ['selfResponsible', 'self_income'],
}

const roleType: Record<BackerRole, ApplicationPersonType> = {
  r: 'financial_responsible',
  g: 'guarantor',
}

function semesterOf(graduation: string, fallback: number | null) {
  if (/^\d{4}-\d{2}$/.test(graduation)) {
    const [year, month] = graduation.split('-').map(Number)
    return estimatedSemester(year, month - 1)
  }
  return fallback ?? 0
}

export function InternshipJourneyPage() {
  const { application_key: applicationKey = '', step: stepParam } = useParams()
  const navigate = useNavigate()
  const application = useReadApplication(applicationKey)
  const user = useReadUserProfile()
  const [form, setForm] = useState<InternshipForm>(emptyForm)
  const [hydratedStamp, setHydratedStamp] = useState<string | null>(null)
  const [collegeSearch, setCollegeSearch] = useState('')
  const [ibge, setIbge] = useState('')
  const [exitOpen, setExitOpen] = useState(false)
  const [saving, setSaving] = useState<'saving' | 'saved' | 'error'>('saved')
  const { confirmed, confirm } = useConfirmedSteps(applicationKey)
  const [error, setError] = useState('')

  const colleges = useColleges(collegeSearch)
  const data = application.data
  const student = data ? studentLink(data) : undefined
  const studentPerson = student?.person
  const stamp = hydrationStamp(applicationKey, studentPerson?.key)
  const hydrated = hydratedStamp?.startsWith(`${applicationKey}:`) ?? false
  const responsibleLinks = useMemo(
    () => (data ? backerLinks(data, 'r') : []),
    [data],
  )
  const guarantorLinks = useMemo(
    () => (data ? backerLinks(data, 'g') : []),
    [data],
  )

  useEffect(() => {
    if (!data || user.isLoading || !shouldRehydrate(hydratedStamp, stamp))
      return
    const base = formFromApplication(data, user.data?.profile?.graduation_date)
    setForm({ ...base, ...readDraft(applicationKey) })
    setHydratedStamp(stamp)
  }, [
    applicationKey,
    data,
    hydratedStamp,
    stamp,
    user.isLoading,
    user.data?.profile?.graduation_date,
  ])

  const facts = useMemo(() => {
    const person = studentPerson
    const people = data?.application_persons ?? []
    const hasDocuments = people.every((link) => {
      if (link.person_types.includes('college')) return true
      const types = link.person_types.includes('student')
        ? studentDocuments
        : backerDocuments
      const attached = new Set(
        link.person.documents?.map((item) => item.type) ?? [],
      )
      return types.every((type) => attached.has(type))
    })
    return {
      hasSimulation: Boolean(data?.simulation),
      status: data?.status ?? 'incomplete',
      serverStep: data?.step ?? 'initial_proponents',
      completed: {
        // a mensalidade só vai para o financial com a simulação: até lá fica no rascunho
        fac: Boolean(
          data?.college &&
            user.data?.profile?.graduation_date &&
            (person?.monthly_tuition || form.tuition),
        ),
        // dados já cadastrados só contam depois de conferidos nesta solicitação
        eu: Boolean(
          person?.birthdate && person.gender && confirmed.includes('eu'),
        ),
        end: Boolean(person?.address?.length && confirmed.includes('end')),
        civil: Boolean(
          person?.marital_status &&
            person.politically_exposed != null &&
            person.us_person != null &&
            confirmed.includes('civil'),
        ),
        traj: Boolean(person?.high_school_type && confirmed.includes('traj')),
        planos: Boolean(
          person?.residency_timing && confirmed.includes('planos'),
        ),
        fin: Boolean(
          person?.tuition_payment_sources?.length &&
            person.financing_reason &&
            confirmed.includes('fin'),
        ),
        resp: Boolean(
          student?.person_types.includes('financial_responsible') &&
            person?.monthly_income != null &&
            confirmed.includes('resp'),
        ),
      },
      selfResponsible: form.selfResponsible === 'yes',
      responsibleCount: responsibleLinks.length,
      guarantorCount: guarantorLinks.length,
      editing: form.editing || null,
      hasDocuments,
    }
  }, [
    data,
    student,
    studentPerson,
    user.data?.profile?.graduation_date,
    form.selfResponsible,
    form.editing,
    form.tuition,
    confirmed,
    responsibleLinks.length,
    guarantorLinks.length,
  ])

  const step = data ? resolveStep(stepParam, facts) : 'fac'
  const steps = visibleSteps(facts)
  const role = editorRole(step)

  useEffect(() => {
    if (!data || !hydrated || stepParam === step) return
    navigate(`/credit/applications/${applicationKey}/${step}`, {
      replace: true,
    })
  }, [data, hydrated, step, stepParam, applicationKey, navigate])

  const addressState = role ? form.backer.address_state : form.address_state
  const municipalities = useMunicipalities(addressState)

  useEffect(() => {
    const match = municipalities.data?.find((item) => item.ibge_code === ibge)
    if (!match) return
    const municipality_id = String(match.id)
    if (role) {
      setForm((current) => ({
        ...current,
        backer: { ...current.backer, municipality_id },
      }))
    } else {
      setForm((current) => ({ ...current, municipality_id }))
    }
    setIbge('')
  }, [municipalities.data, ibge, role])

  const semester = semesterOf(
    form.graduation,
    user.data?.profile?.current_semester ?? null,
  )
  const minSemester = creditProducts.internship.minSemester ?? 8
  const graduationDate = form.graduation ? `${form.graduation}-01` : ''
  const previewStep = step === 'sem' || step === 'divida' || step === 'res'
  const preview = useInternshipPreview(
    previewStep && graduationDate
      ? {
          monthly_tuition: Number(form.tuition),
          graduation_date: graduationDate,
          start_semester: form.startSemester || Math.max(9, semester),
          paid_tuitions: form.paidTuitions,
          college_debt: form.hasDebt ? Number(form.debt) || 0 : 0,
        }
      : null,
  )

  const setCollege = useSetApplicationCollege(applicationKey)
  const saveSimulation = useSaveInternshipSimulation(applicationKey)
  const patchPerson = usePatchPerson(applicationKey)
  const createPerson = useCreateApplicationPerson(applicationKey)
  const setTypes = useSetApplicationPersonTypes(applicationKey)
  const detach = useDetachApplicationPerson(applicationKey)
  const complete = useCompleteProponents(applicationKey)
  const preAnalysis = usePreAnalysis(applicationKey)
  const upload = useUploadDocument(applicationKey)
  const removeDocument = useDeleteDocument(applicationKey)
  const send = useSendCommittee(applicationKey)
  const accept = useAcceptProposal(applicationKey)
  const reissue = useReissueProposal(applicationKey)
  const proposal = data?.committee?.proposal ?? null
  const proposalExpired = proposal ? isProposalExpired(proposal) : false

  const installments = (data?.simulation?.cashflow ?? []).map(
    (item) => item.value,
  )
  const minIncome = minGuarantorIncome(installments)
  const phoneVerified = Boolean(
    user.data?.alias?.find((alias) => alias.type === 'phone' && alias.verified),
  )

  const documentPeople: DocumentPerson[] = (data?.application_persons ?? [])
    .filter((link) => !link.person_types.includes('college'))
    .map((link) => {
      const isStudent = link.person_types.includes('student')
      const responsible = link.person_types.includes('financial_responsible')
      const guarantor = link.person_types.includes('guarantor')
      const role = isStudent
        ? 'Você · estudante'
        : responsible && guarantor
          ? 'Responsável financeiro e avalista'
          : responsible
            ? 'Responsável financeiro'
            : 'Avalista'
      return {
        key: link.person.key,
        name: isStudent
          ? [user.data?.first_name, user.data?.last_name]
              .filter(Boolean)
              .join(' ') || link.person.name
          : link.person.name,
        role,
        types: isStudent ? studentDocuments : backerDocuments,
        student: isStudent,
        documents: link.person.documents ?? [],
      }
    })
  const hasLetter = Boolean(
    studentPerson?.documents?.some((item) => item.type === ENABLING_LETTER),
  )

  const nextDisabled = !isStepValid(step, form, {
    semester,
    minSemester,
    previewViable: preview.data ? preview.data.viable : null,
    hasVerifiedPhone: phoneVerified,
    minGuarantorIncome: minIncome,
    responsibleCount: responsibleLinks.length,
    guarantorCount: guarantorLinks.length,
    hasRequiredDocs: facts.hasDocuments,
    hasLetter,
    hasProposal: Boolean(proposal && !proposal.accepted_at && !proposalExpired),
    denied: data?.status === 'denied',
  })

  function patch(partial: Partial<InternshipForm>) {
    setForm((current) => ({ ...current, ...partial }))
    writeDraft(applicationKey, partial)
  }

  function patchBacker(partial: Partial<BackerForm>) {
    setForm((current) => {
      const backer = { ...current.backer, ...partial }
      writeDraft(applicationKey, { backer })
      return { ...current, backer }
    })
  }

  function go(nextStep: InternshipStep) {
    setError('')
    // fora das telas do cadastro, nenhum responsável ou avalista fica em edição
    if (!editorRole(nextStep) && form.editing) {
      patch({ editing: '', backer: emptyBacker() })
    }
    navigate(`/credit/applications/${applicationKey}/${nextStep}`)
  }

  function back() {
    const previous = previousStep(step, facts)
    if (previous) go(previous)
  }

  function linkTypes(personKey: string) {
    const link = data?.application_persons.find(
      (item) => item.person.key === personKey,
    )
    return (link?.person_types ?? []) as ApplicationPersonType[]
  }

  function openEditor(nextRole: BackerRole, backer: BackerForm) {
    patch({ editing: nextRole, backer })
    go(nextRole === 'r' ? 'r1' : 'g1')
  }

  function onEditBacker(nextRole: BackerRole, key: string) {
    const links = nextRole === 'r' ? responsibleLinks : guarantorLinks
    const link = links.find((item) => item.person.key === key)
    if (link)
      openEditor(nextRole, backerFromPerson(link.person, link.relationship))
  }

  async function onResponsibleGuarantor(key: string, guarantor: boolean) {
    const declined = form.guarantorDeclined.filter((item) => item !== key)
    patch({ guarantorDeclined: guarantor ? declined : [...declined, key] })
    if (guarantor) {
      await setTypes.mutateAsync({
        personKey: key,
        types: Array.from(new Set([...linkTypes(key), 'guarantor' as const])),
      })
      await application.refetch()
    } else if (linkTypes(key).includes('guarantor')) {
      await onRemoveBacker('g', key)
    }
  }

  async function onRemoveBacker(nextRole: BackerRole, key: string) {
    const remaining = linkTypes(key).filter(
      (type) => type !== roleType[nextRole],
    )
    if (remaining.length)
      await setTypes.mutateAsync({ personKey: key, types: remaining })
    else await detach.mutateAsync(key)
    await application.refetch()
  }

  /** Salva uma etapa do cadastro de responsável ou avalista. */
  async function saveBacker(backerRole: BackerRole, index: number) {
    const backer = form.backer
    const type = roleType[backerRole]
    if (index === 1) return
    let key = backer.key
    if (index === 2) {
      if (!key) {
        const created = await createPerson.mutateAsync({
          name: backer.name.trim(),
          tax_id: digits(backer.tax_id),
          birthdate: backer.birthdate,
          email: backer.email.trim(),
          phone: digits(backer.phone),
          types: [type],
          relationship: backer.relationship || null,
        })
        key = created.key
        patchBacker({ key })
      }
      // a mesma pessoa pode já estar na solicitação em outro papel
      const types = Array.from(new Set([...linkTypes(key), type]))
      await setTypes.mutateAsync({
        personKey: key,
        types,
        relationship: backer.relationship || null,
      })
      await patchPerson.mutateAsync({
        personKey: key,
        body: {
          ...backerPayloadForStep(1, backer),
          ...backerPayloadForStep(2, backer),
        },
      })
      return
    }
    await patchPerson.mutateAsync({
      personKey: key,
      body: backerPayloadForStep(index, backer),
    })
  }

  async function next() {
    if (step === 'pre') {
      navigate(routes.credit.index)
      return
    }
    if (step === 'analise' || step === 'fim') {
      clearDraft(applicationKey)
      navigate(routes.credit.index)
      return
    }
    if (step === 'assinar') return
    const following = steps[steps.indexOf(step) + 1]
    try {
      setSaving('saving')
      setError('')
      if (role) {
        const index = Number(step.slice(1))
        await saveBacker(role, index)
        if (index === 4) {
          patch({ editing: '', backer: emptyBacker() })
          await application.refetch()
          go(role === 'r' ? 'rlist' : 'av')
        } else {
          go(`${role}${index + 1}` as InternshipStep)
        }
      } else if (step === 'fac') {
        await setCollege.mutateAsync(form.collegeKey)
        await saveProfile({ graduation_date: graduationDate })
        await application.refetch()
        clearDraftKeys(applicationKey, stepKeys.fac ?? [])
        go('sem')
      } else if (step === 'res') {
        if (!preview.data?.viable) {
          navigate(routes.credit.index)
          setSaving('saved')
          return
        }
        await saveSimulation.mutateAsync({
          monthly_tuition: Number(form.tuition),
          graduation_date: graduationDate,
          start_semester: preview.data.plan.start_semester,
          paid_tuitions: form.paidTuitions,
          college_debt: form.hasDebt ? Number(form.debt) || 0 : 0,
        })
        await application.refetch()
        clearDraftKeys(applicationKey, stepKeys.res ?? [])
        go('eu')
      } else if (
        (studentSteps as readonly string[]).includes(step) &&
        studentPerson
      ) {
        const body = studentPayloadForStep(step, form)
        if (body)
          await patchPerson.mutateAsync({ personKey: studentPerson.key, body })
        await application.refetch()
        clearDraftKeys(applicationKey, stepKeys[step] ?? [])
        confirm(step)
        if (following) go(following)
      } else if (step === 'resp' && studentPerson && student) {
        const self = form.selfResponsible === 'yes'
        const types: ApplicationPersonType[] = self
          ? ['student', 'financial_responsible']
          : ['student']
        await setTypes.mutateAsync({ personKey: studentPerson.key, types })
        if (self) {
          await patchPerson.mutateAsync({
            personKey: studentPerson.key,
            body: { monthly_income: Number(form.self_income) },
          })
        }
        await application.refetch()
        confirm('resp')
        if (self) go('av')
        else if (responsibleLinks.length) go('rlist')
        else openEditor('r', emptyBacker())
      } else if (step === 'rlist') {
        go('av')
      } else if (step === 'av') {
        try {
          await complete.mutateAsync()
        } catch (failure) {
          const details = (
            failure as { details?: { step?: string; person_key?: string } }
          ).details
          await application.refetch()
          const target = internshipSteps.find((item) => item === details?.step)
          if (target) {
            setError('Faltam alguns dados. Confira esta etapa para continuar.')
            setSaving('saved')
            navigate(`/credit/applications/${applicationKey}/${target}`)
            return
          }
          throw failure
        }
        await application.refetch()
        go('scr')
      } else if (step === 'scr' && studentPerson) {
        await patchPerson.mutateAsync({
          personKey: studentPerson.key,
          body: { authorized_scr: true },
        })
        await preAnalysis.mutateAsync()
        const refreshed = await application.refetch()
        go(refreshed.data?.status === 'denied' ? 'analise' : 'pre')
      } else if (step === 'docs') {
        go('carta')
      } else if (step === 'carta') {
        await send.mutateAsync()
        await application.refetch()
        clearDraft(applicationKey)
        clearConfirmedSteps(applicationKey)
        go('analise')
      } else if (step === 'proposta' && proposal) {
        await accept.mutateAsync(proposal.key)
        await application.refetch()
        go('assinar')
      } else if (following) {
        go(following)
      }
      setSaving('saved')
    } catch {
      setSaving('error')
      setError('Não conseguimos salvar. Confira os dados e tente de novo.')
    }
  }

  if (!applicationKey) return <Navigate to={routes.credit.index} replace />
  if (application.isLoading || !data || !hydrated) {
    return <JourneySkeleton />
  }
  if (data.product !== 'internship') {
    return <Navigate to={routes.credit.index} replace />
  }

  const name =
    [user.data?.first_name, user.data?.last_name].filter(Boolean).join(' ') ||
    studentPerson?.name ||
    ''
  const emails =
    user.data?.alias?.filter((alias) => alias.type === 'email') ?? []
  const email =
    (emails.find((alias) => alias.verified) ?? emails[0])?.data ?? ''
  const phone =
    user.data?.alias?.find((alias) => alias.type === 'phone')?.data ?? ''
  const progress = stepProgress(step, facts)
  const notViable = step === 'res' && preview.data && !preview.data.viable
  const nextLabel = notViable
    ? 'Ver outras soluções'
    : (nextLabels[step] ?? 'Continuar')
  const scrPeople = [...responsibleLinks, ...guarantorLinks]
    .filter(
      (link, index, list) =>
        list.findIndex((item) => item.person.key === link.person.key) === index,
    )
    .map((link) => ({
      key: link.person.key,
      name: link.person.name,
      role: link.person_types.includes('financial_responsible')
        ? 'Responsável'
        : 'Avalista',
      email: link.person.email,
    }))

  function onCep(cep: CepResponse) {
    setIbge(cep.ibge_code)
    const address = {
      zip_code: cep.zipcode,
      street: cep.street,
      district: cep.district,
      address_state: cep.state_code,
      municipality_id: '',
    }
    if (role) patchBacker(address)
    else patch(address)
  }

  function onStateChange(stateCode: string) {
    setIbge('')
    if (role) patchBacker({ address_state: stateCode, municipality_id: '' })
    else patch({ address_state: stateCode, municipality_id: '' })
  }

  return (
    <>
      <JourneyShell
        productName="Financiamento Estudantil"
        stage={stageOf(step)}
        progress={progress}
        title={titles[step]}
        hint={hints[step]}
        footerNote={
          error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : undefined
        }
        done={step === 'fim'}
        onBack={
          steps.indexOf(step) > 0 &&
          !['analise', 'fim', 'docs', 'pre'].includes(step)
            ? back
            : undefined
        }
        onExit={() =>
          stageOf(step) < 3 && step !== 'pre'
            ? setExitOpen(true)
            : navigate(routes.credit.index)
        }
        onNext={step === 'assinar' ? undefined : next}
        nextLabel={nextLabel}
        nextDisabled={nextDisabled && !notViable}
        saving={saving}
        showSaved={stageOf(step) < 2}
      >
        <InternshipScreens
          step={step}
          form={form}
          patch={patch}
          patchBacker={patchBacker}
          colleges={
            data.college &&
            !colleges.data?.some((item) => item.key === data.college?.key)
              ? [data.college, ...(colleges.data ?? [])]
              : (colleges.data ?? [])
          }
          onCollegeSearch={setCollegeSearch}
          semester={semester}
          minSemester={minSemester}
          preview={preview.data}
          previewError={preview.isError}
          identity={{
            name,
            taxId: formatTaxId(user.data?.tax_id ?? ''),
            email,
            phone,
          }}
          municipalities={municipalities.data ?? []}
          onCep={onCep}
          onStateChange={onStateChange}
          responsibles={backerRows(responsibleLinks)}
          guarantors={backerRows(guarantorLinks)}
          onEditBacker={onEditBacker}
          onRemoveBacker={onRemoveBacker}
          onResponsibleGuarantor={onResponsibleGuarantor}
          onAddBacker={(nextRole) => openEditor(nextRole, emptyBacker())}
          minGuarantorIncome={minIncome}
          scrPeople={scrPeople}
          documentPeople={documentPeople}
          onUpload={(personKey, type, file) =>
            upload.mutate({ type, file, personKey })
          }
          onRemoveDocument={(documentKey) =>
            removeDocument.mutateAsync(documentKey)
          }
          uploading={
            upload.isPending && upload.variables
              ? {
                  personKey: upload.variables.personKey,
                  type: upload.variables.type,
                }
              : undefined
          }
          phoneVerified={phoneVerified}
          denied={data.status === 'denied'}
          collegeName={data.college?.name ?? 'faculdade'}
          proposal={proposal}
          proposalExpired={proposalExpired}
          reissuing={reissue.isPending}
          onReissue={async () => {
            if (!proposal) return
            await reissue.mutateAsync(proposal.key)
            await application.refetch()
            go('analise')
          }}
        />
      </JourneyShell>

      <JourneySheet
        open={exitOpen}
        title="Sair da solicitação?"
        description="Tudo o que você preencheu fica salvo. Para continuar, abra Solicitações."
        onClose={() => setExitOpen(false)}
      >
        <Button
          variant="contained"
          onClick={() => navigate(routes.credit.applications)}
        >
          Sair e continuar depois
        </Button>
        <Button variant="text" onClick={() => setExitOpen(false)}>
          Continuar preenchendo
        </Button>
      </JourneySheet>
    </>
  )
}
